<template>
    <v-card>
        <v-card-title>Edit {{ attribute.name }} measurement for {{ thing.id }}</v-card-title>

        <v-card-text>
            <v-row>
                <v-col>
                    <thing-form-field
                        v-model="formField"
                        :thing-schema="thingSchema"
                        :experiment="experiment"
                        :protocol="protocol"
                        style="width: 400px; display: inline-block"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <v-text-field
                        :type="dateFieldType"
                        v-model="editCollectionDateTimeISOString"
                        :label="collectionDateTimeLabel"
                        style="width: 320px; display: inline-block"
                    >
                        <template v-slot:append-inner>
                            ({{ this.experiment.siteTimeZone }})
                        </template>
                    </v-text-field>
                </v-col>
            </v-row>
        </v-card-text>

        <v-card-actions>
            <v-spacer />
            <v-btn color="primary" @click="cancel">Cancel</v-btn>
            <v-btn color="primary" @click="updateMeasurementValue">Update Value</v-btn>
        </v-card-actions>
    </v-card>
</template>
<script>
import { defineComponent } from "vue";

import ThingFormField from "../common/ThingFormField.vue";
import { toDateTimeForExperiment } from "@/lib/dateUtils.js";
import { getFormFieldConfigForThingSchemaAttribute } from "@/lib/thingUtils.ts";
import { buildCollectionDateTimeFieldConfig } from "@/lib/measurementsUtils.js";
import { coerceValue } from "@/lib/coercer.js";

export default defineComponent({
    components: { ThingFormField },

    props: {
        thingSchema: {
            type: Object,
            required: true,
        },
        thing: {
            type: Object,
            required: true,
        },
        attribute: {
            type: Object,
            required: true,
        },
        collectionDateTime: {
            type: Object,
            required: false, // required only if there is a measurement value
        },
        value: {
            type: [Number, String, Array],
            required: false,
        },
        readoutGroup: {
            type: Object,
            required: true,
        },
        defaultCollectionDateTime: {
            type: Object, // luxon DateTime object
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
        protocol: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            formField: null,
            dateFieldType: null,
            editCollectionDateTimeISOString: null,
            collectionDateTimeLabel: null,
        };
    },

    watch: {
        thingSchema: {
            handler() {
                this.updateFormField();
            },
            immediate: true,
        },
        thing: {
            handler() {
                this.updateFormField();
            },
            immediate: true,
        },
        attribute: {
            handler() {
                this.updateFormField();
            },
            immediate: true,
        },
        value: {
            handler() {
                this.updateFormField();
            },
            immediate: true,
        },
    },

    methods: {
        updateFormField() {
            this.formField = getFormFieldConfigForThingSchemaAttribute(
                this.attribute,
                this.value,
                this.protocol
            );

            this.updateCollectionDateTimeField();
        },
        updateCollectionDateTimeField() {
            // Note: use DateTime everywhere outside the nearest place where we need to convert it to a String or
            // Date or whatever. This attempts to avoid confusion about what exactly this "date" is and whether it
            // should be converted to the experiment timezone or not.
            const existingDateTimeISOString = this.collectionDateTime;
            const collectionDateTime = existingDateTimeISOString
                ? toDateTimeForExperiment(existingDateTimeISOString, this.experiment)
                : this.defaultCollectionDateTime;

            const { dateFieldType, editCollectionDateTimeISOString, collectionDateTimeLabel } =
                buildCollectionDateTimeFieldConfig(
                    collectionDateTime,
                    this.readoutGroup,
                    this.experiment
                );

            this.dateFieldType = dateFieldType;
            this.editCollectionDateTimeISOString = editCollectionDateTimeISOString;
            this.collectionDateTimeLabel = collectionDateTimeLabel;
        },
        cancel() {
            this.$emit("cancel-edit-measurement");
        },
        updateMeasurementValue() {
            // Again: use DateTime everywhere outside the nearest place where we need to convert it to a String or
            // Date or whatever. This attempts to avoid confusion about what exactly this "date" is and whether it
            // should be converted to the experiment timezone or not.
            const collectionDateTime = toDateTimeForExperiment(
                this.editCollectionDateTimeISOString,
                this.experiment
            );

            // `""` or `null` measurement values mean "remove previous measurement from my user measurement set"
            const rawValue = this.formField.value === "" ? null : this.formField.value;

            const coercedValue = coerceValue(this.attribute, rawValue);

            const params = {
                thing: this.thing,
                attribute: this.attribute,
                value: coercedValue,
                collectionDateTime,
            };

            this.$emit("update-measurement-data-entry-item-value", params);
        },
    },
});
</script>
